import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import {
  Box,
  Button,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import {
  getWeekLabel,
  parseDateLabel,
  sortWeeksByDate,
} from "../../utils/activityUtils";

interface WeeklyActivityTableProps {
  weeklyGrid: any;
  showTestFBOs: boolean;
  onShowTestFBOsChange: (value: boolean) => void;
}

// Constants
const DAYS_PER_WEEK = 7;

// Constants for layout calculations
const HEADER_HEIGHT = 40;
const ROW_HEIGHT = 40;
const FBO_COLUMN_WIDTH = 220; // Increased to prevent text cutoff
const WEEK_COLUMN_WIDTH = 95; // Slightly increased for date labels

export const WeeklyActivityTable: React.FC<WeeklyActivityTableProps> = ({
  weeklyGrid,
  showTestFBOs,
  onShowTestFBOsChange,
}) => {
  const { t } = useTranslation();

  if (!weeklyGrid) {
    return null;
  }

  // Add error handling for edge cases
  const sortedWeeks = sortWeeksByDate(weeklyGrid.weeks || []);
  const filteredFbos =
    weeklyGrid.fbos
      ?.filter(
        (fbo: any) => (showTestFBOs || !fbo.is_test) && fbo.fbo_name !== "FBO"
      )
      .sort((a: any, b: any) => a.fbo_name.localeCompare(b.fbo_name)) || [];

  // Handle empty data
  if (!sortedWeeks.length || !filteredFbos.length) {
    return (
      <Stack spacing={2} sx={{ width: "100%", p: 1 }}>
        <Typography variant="h6">
          {t("screens.superAdmin.weeklyActivityTable.title")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("screens.superAdmin.weeklyActivityTable.noData")}
        </Typography>
      </Stack>
    );
  }

  // Helper function to get the Monday of a given date's week
  const getWeekStart = (date: Date): Date => {
    const weekStart = new Date(date);
    const day = weekStart.getDay();
    const diff = day === 0 ? -6 : 1 - day; // If Sunday (0), go back 6 days; otherwise go to Monday
    weekStart.setDate(weekStart.getDate() + diff);
    return weekStart;
  };

  // Helper function to download TSV file
  const downloadTSV = (content: string, filename: string) => {
    const blob = new Blob([content], {
      type: "text/tab-separated-values;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    // Delay cleanup to ensure download completes in all browsers
    setTimeout(() => URL.revokeObjectURL(url), 100);
  };

  const exportWeeklyActivityTSV = () => {
    if (!weeklyGrid?.fbos || !weeklyGrid?.weeks) return;

    // Prepare and sort FBOs alphabetically
    const sortedFbos = weeklyGrid.fbos
      .filter((fbo: any) => showTestFBOs || !fbo.is_test)
      .sort((a: any, b: any) => a.fbo_name.localeCompare(b.fbo_name));

    // Helper: Get first Monday of a given year
    const getFirstMondayOfYear = (year: number): Date => {
      const jan1 = new Date(year, 0, 1); // January 1st
      const dayOfWeek = jan1.getDay(); // 0 = Sunday, 1 = Monday, etc.

      if (dayOfWeek === 1) {
        // January 1st is already a Monday
        return jan1;
      } else if (dayOfWeek === 0) {
        // January 1st is Sunday, so first Monday is January 2nd
        return new Date(year, 0, 2);
      } else {
        // Find the next Monday
        const daysUntilMonday = 8 - dayOfWeek;
        const firstMonday = new Date(year, 0, 1 + daysUntilMonday);
        return firstMonday;
      }
    };

    // Configuration - dynamically calculate current year's first week
    const currentYear = new Date().getFullYear();
    const START_DATE = getFirstMondayOfYear(currentYear);
    const END_DATE = new Date();
    const HEADERS = ["Date", "Metric", "fbo", "Activity"];

    // Build activity lookup map for performance
    // Map structure: weekStartDate -> Set of FBO names with activity
    const activityMap = new Map<string, Set<string>>();

    sortedFbos.forEach((fbo: any) => {
      Object.entries(fbo.weeks).forEach(([weekLabel, hasActivity]) => {
        if (hasActivity) {
          const weekDate = parseDateLabel(weekLabel);
          const weekKey = weekDate.toISOString().split("T")[0];

          if (!activityMap.has(weekKey)) {
            activityMap.set(weekKey, new Set());
          }
          activityMap.get(weekKey)?.add(fbo.fbo_name);
        }
      });
    });

    const rows: string[][] = [];
    let currentWeekStart = getWeekStart(START_DATE);

    // Process each week
    while (currentWeekStart <= END_DATE) {
      // Check boundaries
      if (currentWeekStart < START_DATE || currentWeekStart > END_DATE) {
        currentWeekStart.setDate(currentWeekStart.getDate() + DAYS_PER_WEEK);
        continue;
      }

      const weekStartStr = currentWeekStart.toISOString().split("T")[0];

      // Add ONE row per FBO per week (no daily breakdown)
      sortedFbos.forEach((fbo: any) => {
        // Use pre-built activity map for O(1) lookup instead of O(n) search
        const hasActivity =
          activityMap.get(weekStartStr)?.has(fbo.fbo_name) || false;

        rows.push([
          weekStartStr,
          "Distinct Count of fbo on All Events",
          fbo.fbo_name,
          hasActivity ? "1" : "0",
        ]);
      });

      // Move to next week (next Monday)
      currentWeekStart.setDate(currentWeekStart.getDate() + DAYS_PER_WEEK);
    }

    // Generate TSV content and download
    const tsvContent = [
      HEADERS.join("\t"),
      ...rows.map((row) => row.join("\t")),
    ].join("\n");

    const filename = `weekly-activity-${
      new Date().toISOString().split("T")[0]
    }.tsv`;
    downloadTSV(tsvContent, filename);
  };

  // Common cell styles
  const cellStyle = {
    padding: "8px 12px",
    minHeight: 40,
    display: "flex",
    alignItems: "center",
    borderRight: "1px solid",
    borderRightColor: "divider",
    borderBottom: "1px solid",
    borderBottomColor: "divider",
    boxSizing: "border-box" as const,
  };

  return (
    <Stack spacing={2} sx={{ width: "100%", p: 1 }}>
      {/* Header with controls */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h6">
          {t("screens.superAdmin.weeklyActivityTable.title")}
        </Typography>
        <Box display="flex" gap={2} alignItems="center">
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={exportWeeklyActivityTSV}
          >
            {t("screens.superAdmin.weeklyActivityTable.exportButton")}
          </Button>
          <FormControlLabel
            control={
              <Switch
                checked={showTestFBOs}
                onChange={(e) => onShowTestFBOsChange(e.target.checked)}
              />
            }
            label={t("screens.superAdmin.weeklyActivityTable.showTestFBOs")}
          />
        </Box>
      </Box>

      {/* Table Container */}
      <Box
        role="table"
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          overflow: "auto",
          maxHeight: 700,
          position: "relative",
          width: "100%",
          backgroundColor: "background.paper",
          "& ::-webkit-scrollbar": {
            width: "12px",
            height: "12px",
          },
          "& ::-webkit-scrollbar-track": {
            backgroundColor: "action.hover",
            borderRadius: "6px",
          },
          "& ::-webkit-scrollbar-thumb": {
            backgroundColor: "text.secondary",
            borderRadius: "6px",
            border: "2px solid",
            borderColor: "action.hover",
            "&:hover": {
              backgroundColor: "text.primary",
            },
          },
        }}
      >
        {/* Table wrapper to ensure proper sticky behavior */}
        <Box
          sx={{
            display: "inline-block",
            minWidth: "100%",
          }}
        >
          {/* Sticky Header Row */}
          <Box
            role="row"
            sx={{
              display: "flex",
              position: "sticky",
              top: 0,
              zIndex: 30,
              backgroundColor: (theme) => theme.palette.mode === 'dark'
                ? theme.palette.grey[800]
                : theme.palette.grey[200],
            }}
          >
            <Box
              role="columnheader"
              sx={{
                ...cellStyle,
                position: "sticky",
                left: 0,
                zIndex: 31,
                minWidth: FBO_COLUMN_WIDTH,
                backgroundColor: (theme) => theme.palette.mode === 'dark'
                  ? theme.palette.grey[800]
                  : theme.palette.grey[200],
                borderRight: "2px solid",
                borderRightColor: "divider",
                // Ensure visibility
                boxShadow: "2px 0 4px rgba(0,0,0,0.1)",
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
                {t("screens.superAdmin.weeklyActivityTable.columns.fbo")}
              </Typography>
            </Box>
            {sortedWeeks.map((week, index) => (
              <Box
                key={`header-${index}`}
                role="columnheader"
                aria-label={`Week of ${getWeekLabel(week)}`}
                sx={{
                  ...cellStyle,
                  minWidth: WEEK_COLUMN_WIDTH,
                  justifyContent: "center",
                  backgroundColor: (theme) => theme.palette.mode === 'dark'
                    ? theme.palette.grey[800]
                    : theme.palette.grey[200],
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.75rem",
                    whiteSpace: "nowrap",
                    lineHeight: 1.2,
                  }}
                >
                  {getWeekLabel(week)}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Metric Rows - Sticky */}
          {/* Total Customers */}
          <Box
            sx={{
              display: "flex",
              position: "sticky",
              top: HEADER_HEIGHT,
              zIndex: 20,
              backgroundColor: (theme) => theme.palette.mode === 'dark'
                ? theme.palette.grey[900]
                : theme.palette.grey[100],
              height: ROW_HEIGHT,
            }}
          >
            <Box
              sx={{
                ...cellStyle,
                position: "sticky",
                left: 0,
                zIndex: 21,
                minWidth: FBO_COLUMN_WIDTH,
                backgroundColor: (theme) => theme.palette.mode === 'dark'
                  ? theme.palette.grey[900]
                  : theme.palette.grey[100],
                borderRight: "2px solid",
                borderRightColor: "divider",
                boxShadow: "2px 0 4px rgba(0,0,0,0.1)",
              }}
            >
              <Typography variant="body2">
                {t(
                  "screens.superAdmin.weeklyActivityTable.metrics.totalCustomers"
                )}
              </Typography>
            </Box>
            {sortedWeeks.map((week, index) => (
              <Box
                key={`total-${index}`}
                sx={{
                  ...cellStyle,
                  minWidth: WEEK_COLUMN_WIDTH,
                  justifyContent: "center",
                  backgroundColor: (theme) => theme.palette.mode === 'dark'
                    ? theme.palette.grey[900]
                    : theme.palette.grey[100],
                  flexShrink: 0,
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  {week.total_customers || 0}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Weekly Active */}
          <Box
            sx={{
              display: "flex",
              position: "sticky",
              top: HEADER_HEIGHT + ROW_HEIGHT,
              zIndex: 20,
              backgroundColor: (theme) => theme.palette.mode === 'dark'
                ? theme.palette.grey[900]
                : theme.palette.grey[100],
              height: ROW_HEIGHT,
            }}
          >
            <Box
              sx={{
                ...cellStyle,
                position: "sticky",
                left: 0,
                zIndex: 21,
                minWidth: FBO_COLUMN_WIDTH,
                backgroundColor: (theme) => theme.palette.mode === 'dark'
                  ? theme.palette.grey[900]
                  : theme.palette.grey[100],
                borderRight: "2px solid",
                borderRightColor: "divider",
                boxShadow: "2px 0 4px rgba(0,0,0,0.1)",
              }}
            >
              <Typography variant="body2">
                {t(
                  "screens.superAdmin.weeklyActivityTable.metrics.weeklyActive"
                )}
              </Typography>
            </Box>
            {sortedWeeks.map((week, index) => (
              <Box
                key={`weekly-${index}`}
                sx={{
                  ...cellStyle,
                  minWidth: WEEK_COLUMN_WIDTH,
                  justifyContent: "center",
                  backgroundColor: (theme) => theme.palette.mode === 'dark'
                    ? theme.palette.grey[900]
                    : theme.palette.grey[100],
                  flexShrink: 0,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: "bold", color: "primary.main" }}
                >
                  {week.weekly_active || 0}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Monthly Active */}
          <Box
            sx={{
              display: "flex",
              position: "sticky",
              top: HEADER_HEIGHT + (ROW_HEIGHT * 2),
              zIndex: 20,
              backgroundColor: (theme) => theme.palette.mode === 'dark'
                ? theme.palette.grey[900]
                : theme.palette.grey[100],
              height: ROW_HEIGHT,
            }}
          >
            <Box
              sx={{
                ...cellStyle,
                position: "sticky",
                left: 0,
                zIndex: 21,
                minWidth: FBO_COLUMN_WIDTH,
                backgroundColor: (theme) => theme.palette.mode === 'dark'
                  ? theme.palette.grey[900]
                  : theme.palette.grey[100],
                borderRight: "2px solid",
                borderRightColor: "divider",
                boxShadow: "2px 0 4px rgba(0,0,0,0.1)",
              }}
            >
              <Typography variant="body2">
                {t(
                  "screens.superAdmin.weeklyActivityTable.metrics.monthlyActive"
                )}
              </Typography>
            </Box>
            {sortedWeeks.map((week, index) => (
              <Box
                key={`monthly-${index}`}
                sx={{
                  ...cellStyle,
                  minWidth: WEEK_COLUMN_WIDTH,
                  justifyContent: "center",
                  backgroundColor: (theme) => theme.palette.mode === 'dark'
                    ? theme.palette.grey[900]
                    : theme.palette.grey[100],
                  flexShrink: 0,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: "bold", color: "secondary.main" }}
                >
                  {week.monthly_active || 0}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Separator */}
          <Box
            sx={{
              display: "flex",
              marginRight: "-1px",
            }}
          >
            <Box
              sx={{
                position: "sticky",
                left: 0,
                zIndex: 10,
                minWidth: FBO_COLUMN_WIDTH,
                height: 8,
                backgroundColor: "divider",
                flexShrink: 0,
                borderRight: "2px solid",
                borderRightColor: "divider",
              }}
            />
            {sortedWeeks.map((week, index) => (
              <Box
                key={`separator-${index}`}
                sx={{
                  minWidth: WEEK_COLUMN_WIDTH,
                  height: 8,
                  backgroundColor: "divider",
                  flexShrink: 0,
                }}
              />
            ))}
          </Box>

          {/* FBO Data Rows */}
          {filteredFbos?.map((fbo: any) => (
            <Box
              key={fbo.fbo_id}
              role="row"
              aria-label={`Activity for ${fbo.fbo_name}`}
              sx={{
                display: "flex",
              }}
            >
              <Box
                role="rowheader"
                aria-label={`FBO ${fbo.fbo_name}`}
                sx={{
                  ...cellStyle,
                  position: "sticky",
                  left: 0,
                  zIndex: 10,
                  minWidth: FBO_COLUMN_WIDTH,
                  backgroundColor: (theme) => theme.palette.mode === 'dark'
                    ? theme.palette.grey[900]
                    : '#ffffff',
                  borderRight: "2px solid",
                  borderRightColor: "divider",
                  // Removed pseudo-element for better performance
                  boxShadow: "2px 0 4px rgba(0,0,0,0.1)",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    width: "100%",
                    fontSize: "0.875rem",
                    lineHeight: 1.5,
                  }}
                  title={fbo.fbo_name}
                >
                  {fbo.fbo_name}
                </Typography>
              </Box>
              {sortedWeeks.map((week, index) => {
                const label = getWeekLabel(week);
                const hasActivity = fbo.weeks?.[label];
                return (
                  <Box
                    key={`${fbo.fbo_id}-week-${index}`}
                    role="cell"
                    aria-label={`${
                      fbo.fbo_name
                    } activity for week of ${label}: ${
                      hasActivity ? "Active" : "Inactive"
                    }`}
                    sx={{
                      ...cellStyle,
                      minWidth: WEEK_COLUMN_WIDTH,
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {hasActivity ? (
                      <CheckIcon fontSize="small" color="success" />
                    ) : (
                      <CloseIcon fontSize="small" color="error" />
                    )}
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Footer */}
      <Box display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
        <Typography variant="body2">
          {t("screens.superAdmin.weeklyActivityTable.footer.totalFBOs")}{" "}
          {filteredFbos?.length || 0}
        </Typography>
      </Box>
    </Stack>
  );
};
